import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateAndUploadReceipt, generateAndUploadShippingForm } from '../utils/generateReceipt';
import checkout from '../services/checkout';

const Confirmation = () => {
  const navigate = useNavigate();
  const [verifiedOrder, setVerifiedOrder] = useState(null);
  const [orderError, setOrderError] = useState(null);
  useEffect(() => {
    let active = true;
    checkout.current().then(({ data }) => {
      if (!active) return;
      if (data.pending) setOrderError('Le paiement n’est pas encore confirmé.');
      else { setVerifiedOrder(data); sessionStorage.setItem('cgci.checkout.completed', 'true'); }
    }).catch(error => { if (active) setOrderError(error.response?.data?.error || error.message); });
    return () => { active = false; };
  }, []);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [formUrl, setFormUrl] = useState(null);
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);
  const [isGeneratingForm, setIsGeneratingForm] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [emailStatus, setEmailStatus] = useState({ sent: false, error: null });
  const [shipmentStatus, setShipmentStatus] = useState({ created: false, error: null, trackingNumber: null });
  // Référence pour éviter les doubles appels
  const documentGenerationRef = React.useRef(false);
  // La référence shipmentCreationRef n'est plus utilisée car la création d'expédition est désactivée dans le frontoffice

  const { 
    actTitle, 
    amount, 
    userMessage, 
    payment: paymentDetails, 
    // shipment: existingShipment, // Variable non utilisée car la création d'expédition est désactivée dans le frontoffice
    transaction_id: locationTransactionId,
    userName,
    userEmail,
    userPhone,
    userAddress,
    userCity,
    userZipcode,
    userCountry,
    firstname,
    lastname,
    birthDate,
    hostName,
    // Nouveau: infos famille passées depuis Payment.js
    withFamily,
    familyMembers
  } = verifiedOrder || {};

  // Générer un ID de transaction s'il est manquant
  const transaction_id = locationTransactionId;

  useEffect(() => () => { if (receiptUrl) URL.revokeObjectURL(receiptUrl); }, [receiptUrl]);
  useEffect(() => () => { if (formUrl) URL.revokeObjectURL(formUrl); }, [formUrl]);

  const sendConfirmationEmail = useCallback(async (receiptUrl, formUrl) => {
    try {

      // Vérifier que les URLs sont valides
      if (!receiptUrl || !formUrl) {
        if (process.env.NODE_ENV !== 'production') {
          // console.error('Les URLs de document sont manquantes');
        } else {
          // console.error('Erreur lors de la génération de document');
        }
        setEmailStatus({ sent: false, error: 'URLs de document manquantes' });
        return;
      }

      // Vérifier que l'email est valide
      if (!userEmail || !userEmail.includes('@')) {
        if (process.env.NODE_ENV !== 'production') {
          // console.error('Email invalide :', userEmail);
        } else {
          // console.error('Email invalide');
        }
        setEmailStatus({ sent: false, error: 'Email invalide' });
        return;
      }

      const response = await checkout.email(transaction_id);
      
      if (response.data.success) {
        setEmailStatus({ sent: true, error: null });
      } else {
        setEmailStatus({ sent: false, error: 'Une erreur est survenue lors de l\'envoi de l\'email.' });
        if (process.env.NODE_ENV !== 'production') {
          // console.error('Échec de l\'envoi de l\'email de confirmation :', response.data);
        } else {
          // console.error('Échec de l\'envoi de l\'email de confirmation');
        }
      }
    } catch (error) {
      // Log détaillé de l'erreur
      if (process.env.NODE_ENV !== 'production') {
        // console.error('Erreur lors de l\'envoi de l\'email de confirmation :', {
        //   message: error.message,
        //   response: error.response?.data,
        //   status: error.response?.status,
        //   headers: error.response?.headers
        // });
      } else {
        // console.error('Erreur lors de l\'envoi de l\'email de confirmation');
      }
      setEmailStatus({ 
        sent: false, 
        error: error.response?.data?.message || error.message || 'Erreur inconnue' 
      });
    }
  }, [userEmail, userName, transaction_id, actTitle, amount]);

  /**
   * Crée une expédition FedEx en utilisant l'API FedEx et met à jour l'entrée shipment existante
   * 
   * Cette fonction effectue les opérations suivantes :
   * 1. Vérifie si une expédition a déjà été créée pour éviter les doublons
   * 2. Récupère les variables d'environnement pour l'API FedEx
   * 3. Construit le payload pour l'API FedEx avec les informations du client
   * 4. Obtient un token d'authentification auprès de l'API FedEx
   * 5. Crée l'expédition via l'API FedEx
   * 6. Extrait le numéro de suivi de la réponse
   * 7. Met à jour ou crée une entrée d'expédition dans la base de données
   * 
   * @param {Object} userDetails - Détails de l'utilisateur pour l'expédition
   * @returns {Promise<Object>} - Résultat de la création d'expédition avec le numéro de suivi
   * 
   * DÉSACTIVÉ : La création d'expédition FedEx est maintenant gérée exclusivement dans le backoffice (ShipmentForm.js)
   */
  const createFedExShipment = useCallback(async (userDetails) => {
    // console.log('La création d\'expédition FedEx dans le frontoffice est désactivée');
    return {
      success: false,
      error: 'La création d\'expédition FedEx est désormais gérée exclusivement par le backoffice'
    };
  }, []);

  useEffect(() => {
    const generateDocuments = async () => {
      // Ne pas exécuter si déjà fait
      if (documentGenerationRef.current) return;
      
      if (verifiedOrder && paymentDetails) {
        try {
          // Marquer comme déjà exécuté
          documentGenerationRef.current = true;
          
          const userDetails = {
            transaction_id,
            actTitle,
            amount,
            paymentMethod: paymentDetails.method || 'Carte bancaire',
            userName: userName || 'Client',
            // Utilisation directe des variables firstname et lastname
            userLastName: lastname || '',
            userFirstName: firstname || '',
            userBirthDate: birthDate || '',
            userEmail: userEmail || 'Non spécifié',
            userPhone: userPhone || 'Non spécifié',
            userAddress: userAddress || 'Adresse non renseignée',
            userCity: userCity || 'Ville non renseignée',
            userZipcode: userZipcode || 'Code postal non renseigné',
            userCountry: userCountry || 'Pays non renseigné',
            hostName: hostName || '',
            // Propager les informations de famille pour affichage dans les PDFs
            withFamily: !!withFamily,
            familyMembers: Array.isArray(familyMembers) ? familyMembers : []
          };


          // Vérifier que tous les champs requis sont présents
          const requiredFields = ['transaction_id', 'actTitle', 'amount', 'paymentMethod'];
          const missingFields = requiredFields.filter(field => !userDetails[field]);

          if (missingFields.length > 0) {
            throw new Error(`Champs manquants : ${missingFields.join(', ')}`);
          }

          // Générer le reçu
          setIsGeneratingReceipt(true);
          const receiptUrl = await generateAndUploadReceipt(userDetails);
          setReceiptUrl(receiptUrl);
          
          // Générer le formulaire d'expédition
          setIsGeneratingForm(true);
          const formUrl = await generateAndUploadShippingForm(userDetails);
          setFormUrl(formUrl);

          // Envoyer l'email de confirmation si un email est disponible
          if (userEmail) {
            await sendConfirmationEmail(receiptUrl, formUrl);
          }
          
          // La création d'expédition FedEx est désormais gérée exclusivement dans le backoffice
          // console.log('La création d\'expédition FedEx est désactivée dans le frontoffice');
          setShipmentStatus({
            created: false,
            error: 'La création d\'expédition FedEx est désormais gérée exclusivement par le backoffice',
            trackingNumber: null
          });
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            // console.error('Erreur détaillée lors de la génération des documents :', error);
          } else {
            // console.error('Erreur lors de la génération des documents');
          }
          setReceiptError(error);
          setFormError(error);
        } finally {
          setIsGeneratingReceipt(false);
          setIsGeneratingForm(false);
        }
      } else {
        // console.warn('Aucun état de paiement disponible', { 
        //   locationState: location.state, 
        //   paymentDetails 
        // });
      }
    };

    generateDocuments();
  }, [
    verifiedOrder, 
    paymentDetails, 
    transaction_id, 
    actTitle, 
    amount, 
    userName, 
    userEmail, 
    userPhone,
    userAddress,
    userCity,
    userZipcode,
    userCountry,
    firstname,
    lastname,
    birthDate,
    hostName,
    withFamily,
    familyMembers,
    sendConfirmationEmail,
    createFedExShipment
  ]);

  const handleDownloadReceipt = async () => {
    if (receiptUrl) {
      try {
        // Récupérer le fichier PDF
        const response = await fetch(receiptUrl);
        const blob = await response.blob();
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Recu_${transaction_id}.pdf`;
        
        // Simuler un clic pour déclencher le téléchargement
        document.body.appendChild(link);
        link.click();
        
        // Nettoyer
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // console.error('Erreur lors du téléchargement du reçu :', error);
        } else {
          // console.error('Erreur lors du téléchargement du reçu');
        }
        // toast.error('Impossible de télécharger le reçu');
      }
    }
  };

  const handleDownloadForm = async () => {
    if (formUrl) {
      try {
        // Récupérer le fichier PDF
        const response = await fetch(formUrl);
        const blob = await response.blob();
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Formulaire_${transaction_id}.pdf`;
        
        // Simuler un clic pour déclencher le téléchargement
        document.body.appendChild(link);
        link.click();
        
        // Nettoyer
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // console.error('Erreur lors du téléchargement du formulaire :', error);
        } else {
          // console.error('Erreur lors du téléchargement du formulaire');
        }
        // toast.error('Impossible de télécharger le formulaire');
      }
    }
  };

  if (!verifiedOrder) return (
    <main className="container py-12" role="status">
      <p>{orderError || 'Vérification de votre commande…'}</p>
      {orderError && <Link to="/payment">Retour au paiement</Link>}
    </main>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <div className="flex justify-center mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-center mb-4 text-gray-800">
          Paiement confirmé
        </h1>
        
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm4 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
              </svg>
              <h2 className="font-semibold text-gray-700">Détails du paiement</h2>
            </div>
            <p><strong>Acte :</strong> {actTitle}</p>
            <p><strong>Montant :</strong> {amount} €</p>
            <p><strong>Statut :</strong> <span className="text-green-600">Payé</span></p>
            {paymentDetails && (
              <div>
                <p><strong>ID de transaction :</strong> {paymentDetails.transaction_id}</p>
                {emailStatus.error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative" role="alert">
                    <strong className="font-bold">Erreur d&apos;envoi d&apos;email : </strong>
                    <span className="block sm:inline">{emailStatus.error}</span>
                  </div>
                )}
                {emailStatus.sent && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 mt-4 rounded relative" role="alert">
                    <span className="block sm:inline">Un email de confirmation a été envoyé à {userEmail}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {shipmentStatus.created && shipmentStatus.trackingNumber && (
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-2m0 0V8m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="font-semibold text-gray-700">Suivi de l&apos;expédition</h2>
              </div>
              <p><strong>Numéro de suivi :</strong> {shipmentStatus.trackingNumber}</p>
              <p><strong>Transporteur :</strong> FEDEX</p>
              <p><strong>Statut :</strong> <span className="text-yellow-600">En préparation</span></p>
              <p><strong>Date d&apos;expédition estimée :</strong> {new Date(new Date().getTime() + 56 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
            </div>
          )}
          {shipmentStatus.error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mt-4 rounded relative" role="alert">
              <strong className="font-bold">Erreur d&apos;expédition : </strong>
              <span className="block sm:inline">L&apos;expédition sera créée manuellement par notre équipe.</span>
            </div>
          )}
        </div>
        
        <div className="mt-6 text-center space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            {userMessage === 'Utilisateur existant mis à jour' 
              ? 'Vos informations ont été mises à jour.' 
              : 'Un nouveau compte a été créé.'}
          </p>
          
          <div className="flex flex-col space-y-2 mt-4">
            {isGeneratingReceipt || isGeneratingForm ? (
              <div className="flex justify-center items-center">
                <svg className="animate-spin h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="ml-2">Génération des documents...</span>
              </div>
            ) : receiptUrl && formUrl ? (
              <div>
                {receiptUrl && (
                  <button 
                    onClick={handleDownloadReceipt} 
                    className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 transition duration-300 inline-flex items-center justify-center w-full mb-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le reçu
                  </button>
                )}
                {formUrl && (
                  <button 
                    onClick={handleDownloadForm} 
                    className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition duration-300 inline-flex items-center justify-center w-full mb-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le formulaire
                  </button>
                )}
                <button 
                  onClick={() => navigate('/')} 
                  className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition duration-300 inline-flex items-center justify-center w-full mb-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Retour à l&apos;accueil
                </button>
              </div>
            ) : receiptError || formError ? (
              <div className="text-red-500">
                <p>Erreur lors de la génération des documents</p>
                {receiptUrl && (
                  <button 
                    onClick={handleDownloadReceipt} 
                    className="bg-green-500 text-white px-6 py-2 mt-2 rounded-md hover:bg-green-600 transition duration-300 inline-flex items-center justify-center w-full mb-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le reçu
                  </button>
                )}
                {formUrl && (
                  <button 
                    onClick={handleDownloadForm} 
                    className="bg-blue-500 text-white px-6 py-2 mt-2 ml-2 rounded-md hover:bg-blue-600 transition duration-300 inline-flex items-center justify-center w-full mb-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Télécharger le formulaire
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
